import React, { useEffect, useMemo, useState } from "react";
import { Amplify, API, graphqlOperation } from "aws-amplify";
import {
  searchAdminErrorLogs,
  searchSharedApps,
} from "../../graphql/queries";
import { searchAdminErrorLogsCompat } from "../../graphql/admin-error-log-queries";
import { deleteAdminErrorLog, updateAdminErrorLog } from "../../graphql/mutations";
import {
  Heading,
  Button as LumosButton,
  AlertInline,
  Modal,
  ModalControls,
  Icon,
  useNotify,
} from "@loanpal/lumos__core";
import { Box } from "theme-ui";
import Loading from "../../utils/loading";
import NavBar from "../../shared/nav-bar";
import { formFields, components } from "../../utils/authentication";
import { dateTimeStr } from "../../utils/format";
import {
  classifyAdminError,
  adminErrorCategoryStyle,
  salesforceHref,
  sharedAppEditHref,
  requestFormHref,
  resolveSearchToClientId,
  filterPartnerSearchOptions,
  truncateText,
  initiativeTableLabel,
  initiativeDisplayName,
  getAdminErrorLogDateInputValue,
  getAdminErrorLogDateRange,
  formatAdminErrorLogDateRangeLabel,
  ADMIN_ERROR_LOG_DATE_PRESETS,
  getAdminErrorLogPresetDateRange,
  resolveAdminErrorLogDatePreset,
  buildAdminErrorLogSearchFilter,
  ADMIN_ERROR_LOG_FIELD_LABELS,
  ADMIN_ERROR_LOG_MODAL_FIELDS,
  ADMIN_ERROR_RESOLUTION_STATUS,
  adminErrorResolutionLabel,
  normalizeAdminErrorResolutionStatus,
  isAdminErrorResolutionFieldError,
  mergeAdminErrorLogsResolution,
  setLocalAdminErrorResolution,
  ADMIN_ERROR_RESOLUTION_COLUMN_TITLE,
  ADMIN_ERROR_RESOLUTION_PENDING_CELL_TITLE,
  adminErrorResolutionPendingTooltip,
  adminErrorResolutionActiveTooltip,
  adminErrorResolutionSwitchTooltip,
  isAdminErrorEmailClaimLog,
} from "../../utils/admin-error-log";
import {
  formatEmailSentStatus,
  fixSubmittingUserAbsoluteUrl,
  logShouldIncludeFixSubmittingUserLink,
  resolveAdminErrorEmailTemplate,
} from "../../utils/admin-error-email-templates";
import { sendAdminErrorEmail } from "../../utils/api";
import SendAdminErrorEmailModal from "./send-error-email-modal";
import salesforceLogo from "../../images/salesforce-cloud.svg";
import goodleapMark from "../../images/oo-dark-orange.svg";
import {
  Authenticator,
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  View,
  Flex,
  Autocomplete,
  HighlightMatch,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import "../admin-error-log-search.css";

import awsExports from "../../aws-exports";

Amplify.configure(awsExports);

const RESOLUTION_STATUS_ACTIONS = [
  {
    status: ADMIN_ERROR_RESOLUTION_STATUS.FIXED,
    className: "admin-error-log-resolution-fixed",
    renderSymbol: () => <Icon variant="check" size="sm" />,
  },
  {
    status: ADMIN_ERROR_RESOLUTION_STATUS.FALSE_POSITIVE,
    className: "admin-error-log-resolution-false-positive",
    renderSymbol: () => <Icon variant="minus-circle" size="sm" />,
  },
  {
    status: ADMIN_ERROR_RESOLUTION_STATUS.NEEDS_FIX,
    className: "admin-error-log-resolution-needs-fix",
    renderSymbol: () => <Icon variant="times" size="sm" />,
  },
];

const AdminErrorLogs = () => {
  const [appsOptions, setAppsOptions] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [activeClientId, setActiveClientId] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [isOpen, toggleOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showRefreshSnack, setShowRefreshSnack] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [isSendEmailOpen, setIsSendEmailOpen] = useState(false);
  const [sendEmailLog, setSendEmailLog] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [updatingResolutionId, setUpdatingResolutionId] = useState("");
  const todayInputValue = getAdminErrorLogDateInputValue(new Date());
  const [startLogDate, setStartLogDate] = useState(todayInputValue);
  const [endLogDate, setEndLogDate] = useState(todayInputValue);
  const { addSnack } = useNotify();

  const activeDatePreset = useMemo(
    () => resolveAdminErrorLogDatePreset(startLogDate, endLogDate),
    [startLogDate, endLogDate]
  );
  const displayedErrorCount = errorLogs.length;

  const filteredOptions = useMemo(
    () => filterPartnerSearchOptions(searchValue, appsOptions),
    [searchValue, appsOptions]
  );

  useEffect(() => {
    fetchSharedApps();
    fetchErrorLogs({
      clientId: "",
      startDate: startLogDate,
      endDate: endLogDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    document.documentElement.classList.add("admin-error-log-details-open");
    document.body.classList.add("admin-error-log-details-open");

    return () => {
      document.documentElement.classList.remove("admin-error-log-details-open");
      document.body.classList.remove("admin-error-log-details-open");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isSendEmailOpen) return undefined;

    document.documentElement.classList.add("admin-error-log-send-email-open");
    document.body.classList.add("admin-error-log-send-email-open");

    return () => {
      document.documentElement.classList.remove("admin-error-log-send-email-open");
      document.body.classList.remove("admin-error-log-send-email-open");
    };
  }, [isSendEmailOpen]);

  const onChange = (event) => {
    setSearchValue(event.target.value);
  };

  const onSelect = (option) => {
    if (!option?.id) return;
    setSearchValue(option.label);
    applyClientFilter(option.id);
  };

  const onClear = () => {
    setSearchValue("");
    setActiveClientId("");
    fetchErrorLogs({
      clientId: "",
      startDate: startLogDate,
      endDate: endLogDate,
    });
  };

  const applyClientFilter = (clientId) => {
    const normalized = String(clientId || "").trim();
    if (!normalized) {
      onClear();
      return;
    }
    setActiveClientId(normalized);
    fetchErrorLogs({
      clientId: normalized,
      startDate: startLogDate,
      endDate: endLogDate,
    });
  };

  const applyLogDateRange = (nextStart, nextEnd) => {
    const { startValue, endValue } = getAdminErrorLogDateRange(nextStart, nextEnd);
    setStartLogDate(startValue);
    setEndLogDate(endValue);
    fetchErrorLogs({
      clientId: activeClientId,
      startDate: startValue,
      endDate: endValue,
    });
  };

  const handleStartLogDateChange = (event) => {
    const nextStart = String(event.target.value ?? "").trim();
    if (!nextStart || nextStart === startLogDate) return;
    applyLogDateRange(nextStart, endLogDate);
  };

  const handleEndLogDateChange = (event) => {
    const nextEnd = String(event.target.value ?? "").trim();
    if (!nextEnd || nextEnd === endLogDate) return;
    applyLogDateRange(startLogDate, nextEnd);
  };

  const handleDatePresetSelect = (presetId) => {
    const { startValue, endValue } = getAdminErrorLogPresetDateRange(presetId);
    if (startValue === startLogDate && endValue === endLogDate) return;
    applyLogDateRange(startValue, endValue);
  };

  async function resolveAndApplySearch(query) {
    const q = String(query ?? "").trim();
    if (!q) {
      onClear();
      return;
    }

    let resolved = resolveSearchToClientId(q, appsOptions);
    if (!resolved && /\s/.test(q)) {
      resolved = await resolveClientIdFromApi(q);
    }

    if (resolved?.clientId) {
      const match = appsOptions.find((option) => option.id === resolved.clientId);
      setSearchValue(match?.label ?? resolved.clientId);
      applyClientFilter(resolved.clientId);
      return;
    }

    setErrorLogs([]);
    setActiveClientId("");
  }

  async function resolveClientIdFromApi(query) {
    try {
      const data = await API.graphql(
        graphqlOperation(searchSharedApps, {
          filter: {
            or: [
              { clientId: { eq: query } },
              { partnerName: { wildcard: `*${query}*` } },
            ],
          },
          limit: 5,
        })
      );
      const items = data.data.searchSharedApps.items ?? [];
      if (items.length === 0) return null;
      const exactName = items.find(
        (app) =>
          String(app.partnerName ?? "").toLowerCase() === query.toLowerCase()
      );
      const app = exactName ?? items[0];
      return { clientId: app.clientId, matchedBy: "accountName" };
    } catch (err) {
      console.log("error resolving clientId from API", err);
      return null;
    }
  }

  const renderOption = (option, query) => {
    const { label } = option;
    return (
      <Flex alignItems="center" className="admin-error-log-search-option">
        <HighlightMatch style={styles.optionLabel} query={query}>
          {label}
        </HighlightMatch>
      </Flex>
    );
  };

  async function fetchSharedApps() {
    try {
      const appData = await API.graphql(
        graphqlOperation(searchSharedApps, {
          sort: { field: "clientId", direction: "asc" },
          limit: 1000,
        })
      );
      const items = appData.data.searchSharedApps.items;
      if (items.length > 0) {
        const options = items.map((app) => ({
          id: app.clientId,
          label: app.partnerName
            ? `${app.partnerName} (${app.clientId})`
            : app.clientId,
          key: app.id,
        }));
        setAppsOptions(options);
      }
    } catch (err) {
      console.log("error fetching Shared Apps", err);
    }
  }

  async function fetchErrorLogs({
    clientId = "",
    startDate = startLogDate,
    endDate = endLogDate,
    silent = false,
  } = {}) {
    const variables = {
      filter: buildAdminErrorLogSearchFilter({
        clientId,
        startDate,
        endDate,
      }),
      sort: { field: "createdAt", direction: "desc" },
      limit: 500,
    };

    try {
      if (!silent) setSpinner(true);
      setFetchError("");

      let useResolutionField = true;
      let data = null;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const query = useResolutionField
            ? searchAdminErrorLogs
            : searchAdminErrorLogsCompat;
          data = await API.graphql(graphqlOperation(query, variables));
          break;
        } catch (err) {
          if (
            attempt === 0 &&
            useResolutionField &&
            isAdminErrorResolutionFieldError(err)
          ) {
            useResolutionField = false;
            continue;
          }
          throw err;
        }
      }

      const items = mergeAdminErrorLogsResolution(
        data?.data?.searchAdminErrorLogs?.items ?? []
      ).filter((log) => !isAdminErrorEmailClaimLog(log));
      setErrorLogs(items);
      if (!silent) setSpinner(false);
      return true;
    } catch (err) {
      if (!silent) setSpinner(false);
      const message =
        err?.errors?.[0]?.message ||
        err?.message ||
        "Could not load error logs.";
      setFetchError(message);
      console.log("error fetching AdminErrorLogs", err);
      return false;
    }
  }

  const openLogDetails = (log) => {
    setSelectedLog(log);
    toggleOpen(true);
  };

  const openSendEmailModal = (log) => {
    if (!log?.id) return;
    if (isOpen) {
      toggleOpen(false);
      setSelectedLog(null);
    }
    setSendEmailLog(log);
    setIsSendEmailOpen(true);
  };

  const closeSendEmailModal = () => {
    setIsSendEmailOpen(false);
    setSendEmailLog(null);
    setSendingEmail(false);
  };

  const handleSendAdminErrorEmail = async (toEmails) => {
    if (!sendEmailLog?.id) return;
    try {
      setSendingEmail(true);
      await sendAdminErrorEmail(sendEmailLog.id, toEmails);
      addSnack({
        variant: "success",
        message: "Test email sent.",
        duration: 5000,
      });
      setErrorLogs((prev) =>
        prev.map((row) =>
          row.id === sendEmailLog.id ? { ...row, notifyEmail: true } : row
        )
      );
      closeSendEmailModal();
    } catch (err) {
      console.log("error sending admin error email", err);
      addSnack({
        variant: "danger",
        message: "Failed to send error email. Check CloudWatch logs.",
        duration: 5000,
      });
      setSendingEmail(false);
    }
  };

  const refreshTable = (silent = false) => {
    return fetchErrorLogs({
      clientId: activeClientId,
      startDate: startLogDate,
      endDate: endLogDate,
      silent,
    });
  };

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    const startedAt = Date.now();
    try {
      const ok = await refreshTable(true);
      addSnack({
        variant: ok ? "success" : "danger",
        message: ok ? "Error log updated" : "Failed to refresh error log",
        duration: ok ? 2500 : 4000,
      });
    } finally {
      const remaining = Math.max(0, 1000 - (Date.now() - startedAt));
      window.setTimeout(() => setIsRefreshing(false), remaining);
    }
  };

  useEffect(() => {
    const runAutoRefresh = async () => {
      setShowRefreshSnack(true);
      try {
        await refreshTable(true);
      } finally {
        window.setTimeout(() => setShowRefreshSnack(false), 1200);
      }
    };

    const intervalId = window.setInterval(runAutoRefresh, 60_000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId, startLogDate, endLogDate]);

  const updateErrorLogResolution = async (log, nextStatus) => {
    if (!log?.id || updatingResolutionId === log.id) return;

    const normalizedNext = normalizeAdminErrorResolutionStatus(nextStatus);
    const normalizedCurrent = normalizeAdminErrorResolutionStatus(
      log.resolutionStatus
    );
    if (normalizedNext === normalizedCurrent) return;

    try {
      setUpdatingResolutionId(log.id);
      let nextResolutionStatus = normalizedNext;

      try {
        const data = await API.graphql(
          graphqlOperation(updateAdminErrorLog, {
            input: {
              id: log.id,
              resolutionStatus: normalizedNext,
            },
          })
        );
        const updated = data?.data?.updateAdminErrorLog;
        nextResolutionStatus =
          normalizeAdminErrorResolutionStatus(updated?.resolutionStatus) ??
          normalizedNext;
        setLocalAdminErrorResolution(log.id, null);
      } catch (err) {
        if (isAdminErrorResolutionFieldError(err)) {
          setLocalAdminErrorResolution(log.id, normalizedNext);
        } else {
          throw err;
        }
      }

      setErrorLogs((prev) =>
        prev.map((item) =>
          item.id === log.id
            ? { ...item, resolutionStatus: nextResolutionStatus }
            : item
        )
      );
      if (selectedLog?.id === log.id) {
        setSelectedLog((prev) =>
          prev ? { ...prev, resolutionStatus: nextResolutionStatus } : prev
        );
      }
    } catch (err) {
      console.log("error updating AdminErrorLog resolution", err);
      addSnack({
        variant: "danger",
        message: "Could not update review status. Try again.",
        duration: 4000,
      });
    } finally {
      setUpdatingResolutionId("");
    }
  };

  const renderResolutionStatusCell = (log) => {
    const status = normalizeAdminErrorResolutionStatus(log.resolutionStatus);
    const busy = updatingResolutionId === log.id;
    const clearStatus = () => updateErrorLogResolution(log, null);
    const setStatus = (nextStatus) => () =>
      updateErrorLogResolution(log, nextStatus);

    if (!status) {
      return (
        <div
          className="admin-error-log-resolution-cell admin-error-log-resolution-pending"
          title={ADMIN_ERROR_RESOLUTION_PENDING_CELL_TITLE}
        >
          {RESOLUTION_STATUS_ACTIONS.map((action) => {
            const tooltip = adminErrorResolutionPendingTooltip(action.status);
            return (
              <button
                key={action.status}
                type="button"
                className={`admin-error-log-resolution-pending-action admin-error-log-resolution-tooltip ${action.className}`}
                aria-label={tooltip}
                data-tooltip={tooltip}
                disabled={busy}
                onClick={setStatus(action.status)}
              >
                {action.renderSymbol()}
              </button>
            );
          })}
        </div>
      );
    }

    const activeAction = RESOLUTION_STATUS_ACTIONS.find(
      (action) => action.status === status
    );
    const alternateActions = RESOLUTION_STATUS_ACTIONS.filter(
      (action) => action.status !== status
    );

    if (!activeAction) return null;

    const activeTooltip = adminErrorResolutionActiveTooltip(activeAction.status);

    return (
      <div
        className="admin-error-log-resolution-cell"
        title={activeTooltip}
      >
        <button
          type="button"
          className={`admin-error-log-resolution-icon admin-error-log-resolution-tooltip ${activeAction.className}`}
          aria-label={activeTooltip}
          data-tooltip={activeTooltip}
          disabled={busy}
          onClick={clearStatus}
        >
          {activeAction.renderSymbol()}
        </button>
        <div className="admin-error-log-resolution-alt-group">
          {alternateActions.map((action) => {
            const tooltip = adminErrorResolutionSwitchTooltip(action.status);
            return (
              <button
                key={action.status}
                type="button"
                className={`admin-error-log-resolution-alt admin-error-log-resolution-tooltip ${action.className}`}
                aria-label={tooltip}
                data-tooltip={tooltip}
                disabled={busy}
                onClick={setStatus(action.status)}
              >
                {action.renderSymbol()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const promptDeleteErrorLog = (log) => {
    if (!log?.id) return;
    setPendingDeleteId(log.id);
  };

  const cancelDelete = () => {
    setPendingDeleteId(null);
  };

  const confirmDeleteErrorLog = async (log) => {
    if (!log?.id) return;

    try {
      setSpinner(true);
      await API.graphql(
        graphqlOperation(deleteAdminErrorLog, { input: { id: log.id } })
      );
      setErrorLogs((prev) => prev.filter((item) => item.id !== log.id));
      if (selectedLog?.id === log.id) {
        setSelectedLog(null);
        toggleOpen(false);
      }
      setPendingDeleteId(null);
    } catch (err) {
      console.log("error deleting AdminErrorLog", err);
      addSnack({
        variant: "danger",
        message: "Error deleting entry. Please try again.",
        duration: 4000,
      });
    } finally {
      setSpinner(false);
    }
  };

  const renderModalFieldValue = (key, fieldValue, log) => {
    if (key === "createdAt" || key === "updatedAt") {
      return dateTimeStr(fieldValue);
    }
    if (key === "formUrl" && fieldValue) {
      return (
        <a
          href={fieldValue}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.link}
        >
          {fieldValue}
        </a>
      );
    }
    if (key === "salesforceUrl" && fieldValue) {
      return (
        <a
          href={fieldValue}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.link}
        >
          {fieldValue}
        </a>
      );
    }
    if (key === "salesforceUrl" && !fieldValue && salesforceHref(log)) {
      return (
        <a
          href={salesforceHref(log)}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.link}
        >
          {salesforceHref(log)}
        </a>
      );
    }
    if (key === "sharedAppId" && fieldValue) {
      const href = sharedAppEditHref(fieldValue);
      return (
        <a
          href={href}
          style={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          title={fieldValue}
        >
          {fieldValue}
        </a>
      );
    }
    if (key === "requestId" && fieldValue) {
      const href = requestFormHref(fieldValue);
      return (
        <a
          href={href}
          style={styles.link}
          title={fieldValue}
          target="_blank"
          rel="noopener noreferrer"
        >
          {fieldValue}
        </a>
      );
    }
    if (key === "publicFixLink") {
      const requestId = String(log?.requestId ?? "").trim();
      if (!requestId || !logShouldIncludeFixSubmittingUserLink(log)) {
        return "N/A";
      }
      const href = fixSubmittingUserAbsoluteUrl(requestId, log?.sharedAppId);
      if (!href) return "N/A";
      return (
        <a
          href={href}
          style={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          title={href}
        >
          Fix submitting user
        </a>
      );
    }
    if (key === "initiative") {
      const label = initiativeDisplayName(fieldValue);
      if (!label) return "N/A";
      return fieldValue && fieldValue !== label
        ? `${label} (${fieldValue})`
        : label;
    }
    if (key === "notifyEmail") {
      const emailStatus = formatEmailSentStatus(fieldValue);
      return (
        <span>
          {emailStatus.label}{" "}
          (
          <button
            type="button"
            style={styles.linkButton}
            onClick={() => openSendEmailModal(log)}
          >
            Send email
          </button>
          )
        </span>
      );
    }
    if (key === "resolutionStatus") {
      return adminErrorResolutionLabel(fieldValue);
    }
    if (typeof fieldValue === "boolean") {
      return fieldValue.toString();
    }
    return fieldValue || "N/A";
  };

  return (
    <Authenticator
      formFields={formFields}
      components={components}
      hideSignUp={true}
    >
      {({ signOut }) => (
        <>
          {showRefreshSnack && (
            <div
              className="admin-error-log-refresh-snack"
              role="status"
              aria-live="polite"
            >
              Refreshing…
            </div>
          )}
          {spinner && <Loading />}
          <NavBar>
            <LumosButton onClick={signOut} style={styles.signOut}>
              Sign out
            </LumosButton>
          </NavBar>
          <div style={styles.container}>
            <Box>
              <Heading
                sx={{
                  backgroundColor: "#f5f5f5",
                  marginBottom: "12px",
                  marginTop: "20px",
                  fontSize: "25px",
                }}
              >
                <Flex
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  wrap="nowrap"
                  gap="1rem"
                >
                  <View>
                    <div style={styles.headingTitle}>
                      Error Log
                      {activeClientId ? (
                        <span style={styles.count}> — {activeClientId}</span>
                      ) : null}
                    </div>
                    <div style={styles.headingSubtitle}>
                      <span style={styles.countBold}>{displayedErrorCount}</span>
                      <span style={styles.count}>
                        {" "}
                        error{displayedErrorCount === 1 ? "" : "s"} ·{" "}
                        {formatAdminErrorLogDateRangeLabel(
                          startLogDate,
                          endLogDate
                        )}
                      </span>
                    </div>
                  </View>
                  <button
                    type="button"
                    style={styles.refreshButton}
                    className={
                      isRefreshing ? "admin-error-log-refresh-spinning" : undefined
                    }
                    aria-label="Refresh table"
                    title="Refresh table"
                    disabled={isRefreshing}
                    onClick={handleManualRefresh}
                  >
                    <Icon variant="sync" size="sm" />
                  </button>
                </Flex>
              </Heading>
              <Box className="admin-error-log-date-filter" sx={styles.dateFilterBar}>
                <Flex alignItems="center" wrap="wrap" gap="10px">
                  <span style={styles.dateFilterLabel}>Date range</span>
                  <Flex alignItems="center" wrap="wrap" gap="8px">
                    {ADMIN_ERROR_LOG_DATE_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        style={{
                          ...styles.datePresetButton,
                          ...(activeDatePreset === preset.id
                            ? styles.datePresetButtonActive
                            : {}),
                        }}
                        aria-pressed={activeDatePreset === preset.id}
                        onClick={() => handleDatePresetSelect(preset.id)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </Flex>
                  <Flex alignItems="center" wrap="wrap" gap="12px">
                    <label style={styles.dateInputLabel}>
                      From
                      <input
                        type="date"
                        value={startLogDate}
                        max={endLogDate}
                        onChange={handleStartLogDateChange}
                        className="admin-error-log-date-input"
                        aria-label="Error log start date"
                      />
                    </label>
                    <label style={styles.dateInputLabel}>
                      To
                      <input
                        type="date"
                        value={endLogDate}
                        min={startLogDate}
                        max={todayInputValue}
                        onChange={handleEndLogDateChange}
                        className="admin-error-log-date-input"
                        aria-label="Error log end date"
                      />
                    </label>
                  </Flex>
                </Flex>
              </Box>
              <Box className="admin-error-log-search" style={styles.searchBox}>
                <Autocomplete
                  label=""
                  placeholder="Account name or client Id..."
                  size="small"
                  onChange={onChange}
                  onClear={onClear}
                  onSelect={onSelect}
                  onSubmit={(submitted) => {
                    const term =
                      typeof submitted === "string"
                        ? submitted
                        : searchValue;
                    resolveAndApplySearch(term);
                  }}
                  value={searchValue}
                  options={filteredOptions}
                  renderOption={renderOption}
                />
              </Box>
              {fetchError ? (
                <AlertInline variant="danger">{fetchError}</AlertInline>
              ) : null}
              {errorLogs.length > 0 ? (
                <Table
                  className="admin-error-log-table"
                  caption=""
                  highlightOnHover={false}
                  size="small"
                  variation="striped"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell as="th">Date</TableCell>
                      <TableCell as="th">Client Id</TableCell>
                      <TableCell as="th">Error Type</TableCell>
                      <TableCell
                        as="th"
                        style={styles.resolutionCell}
                        title={ADMIN_ERROR_RESOLUTION_COLUMN_TITLE}
                      >
                        Status
                      </TableCell>
                      <TableCell as="th">Initiative</TableCell>
                      <TableCell as="th" style={styles.emailSentCell} title="Click to send email">
                        Email Sent
                      </TableCell>
                      <TableCell as="th" style={styles.logoCell}>
                        Salesforce
                      </TableCell>
                      <TableCell as="th" style={styles.logoCell}>
                        GoodLink
                      </TableCell>
                      <TableCell as="th" style={styles.logoCell}>
                        Details
                      </TableCell>
                      <TableCell as="th" style={styles.logoCell}>
                        Delete
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorLogs.map((log, index) => {
                      const category = classifyAdminError(log.error);
                      const sfUrl = salesforceHref(log);
                      return (
                        <TableRow key={log.id ? log.id : index}>
                          <TableCell>{dateTimeStr(log.createdAt)}</TableCell>
                          <TableCell title={log.clientId || ""}>
                            {truncateText(log.clientId, 36) || "—"}
                          </TableCell>
                          <TableCell>
                            <span
                              style={{
                                ...styles.categoryBadge,
                                ...adminErrorCategoryStyle(category),
                              }}
                            >
                              {category}
                            </span>
                          </TableCell>
                          <TableCell
                            style={styles.resolutionCell}
                            className="admin-error-log-resolution-td"
                          >
                            {renderResolutionStatusCell(log)}
                          </TableCell>
                          <TableCell
                            title={
                              initiativeDisplayName(log.initiative) ||
                              log.initiative ||
                              ""
                            }
                          >
                            {initiativeTableLabel(log.initiative) || "—"}
                          </TableCell>
                          <TableCell style={styles.emailSentCell}>
                            {(() => {
                              const emailStatus = formatEmailSentStatus(
                                log.notifyEmail
                              );
                              return (
                                <button
                                  type="button"
                                  style={{
                                    ...styles.linkButton,
                                    ...styles.emailSentLabel,
                                    ...(emailStatus.tone === "sent"
                                      ? styles.emailSentSent
                                      : emailStatus.tone === "failed"
                                      ? styles.emailSentFailed
                                      : styles.emailSentNotSent),
                                  }}
                                  onClick={() => openSendEmailModal(log)}
                                  title={
                                    log.emailTemplateId ||
                                    resolveAdminErrorEmailTemplate(
                                      log.error,
                                      log.emailTemplateId
                                    )?.id ||
                                    "Send error email"
                                  }
                                >
                                  {emailStatus.label}
                                </button>
                              );
                            })()}
                          </TableCell>
                          <TableCell style={styles.logoCell}>
                            {sfUrl ? (
                              <a
                                href={sfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.logoLink}
                                title={log.accountName || "Salesforce"}
                                aria-label={`Open Salesforce${
                                  log.accountName ? ` for ${log.accountName}` : ""
                                }`}
                              >
                                <img
                                  src={salesforceLogo}
                                  alt=""
                                  style={styles.salesforceLogo}
                                />
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell style={styles.logoCell}>
                            {log.formUrl ? (
                              <a
                                href={log.formUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.logoLink}
                                title="Open GoodLink"
                                aria-label="Open GoodLink"
                              >
                                <img
                                  src={goodleapMark}
                                  alt=""
                                  style={styles.goodleapMark}
                                />
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell style={styles.logoCell}>
                            <button
                              type="button"
                              style={styles.iconButton}
                              aria-label="View error details"
                              onClick={() => openLogDetails(log)}
                            >
                              <Icon variant="eye-open" size="sm" />
                            </button>
                          </TableCell>
                          <TableCell style={styles.logoCell}>
                            {pendingDeleteId === log.id ? (
                              <div style={styles.deleteConfirm}>
                                <button
                                  type="button"
                                  style={styles.deleteConfirmButton}
                                  onClick={() => confirmDeleteErrorLog(log)}
                                >
                                  Confirm delete
                                </button>
                                <button
                                  type="button"
                                  style={styles.deleteCancelButton}
                                  aria-label="Cancel delete"
                                  title="Cancel"
                                  onClick={cancelDelete}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                style={styles.iconButton}
                                aria-label="Delete error log entry"
                                title="Delete"
                                onClick={() => promptDeleteErrorLog(log)}
                              >
                                <Icon variant="trash" size="sm" />
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <AlertInline variant="info">No items found.</AlertInline>
              )}
            </Box>
            <Box sx={{ marginBottom: "100px" }}>&nbsp;</Box>
          </div>
          <Modal
            variant="large"
            isOpen={isOpen}
            onDismiss={() => toggleOpen(false)}
            title="Error details"
          >
            <Box sx={{ padding: "10px 0" }}>
              {selectedLog && (
                <>
                  <Box sx={{ marginBottom: "16px" }}>
                    <span
                      style={{
                        ...styles.categoryBadge,
                        ...adminErrorCategoryStyle(
                          classifyAdminError(selectedLog.error)
                        ),
                      }}
                    >
                      {classifyAdminError(selectedLog.error)}
                    </span>
                  </Box>
                  <Box sx={{ marginBottom: "16px", whiteSpace: "pre-wrap" }}>
                    {selectedLog.error}
                  </Box>
                  <Table
                    caption=""
                    highlightOnHover={false}
                    size="small"
                    variation="striped"
                  >
                    <TableBody>
                      {ADMIN_ERROR_LOG_MODAL_FIELDS.map((key) => (
                        <TableRow key={key}>
                          <TableCell
                            as="th"
                            style={{ fontWeight: "bold", width: "220px" }}
                          >
                            {ADMIN_ERROR_LOG_FIELD_LABELS[key] || key}
                          </TableCell>
                          <TableCell>
                            {renderModalFieldValue(
                              key,
                              selectedLog[key],
                              selectedLog
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
            <ModalControls
              actions={[
                {
                  onClick: () => {
                    if (selectedLog) openSendEmailModal(selectedLog);
                  },
                  label: "Send email",
                },
                {
                  onClick: () => toggleOpen(false),
                  label: "Close",
                },
              ]}
            />
          </Modal>
          <SendAdminErrorEmailModal
            log={sendEmailLog}
            isOpen={isSendEmailOpen}
            sending={sendingEmail}
            onDismiss={closeSendEmailModal}
            onSend={handleSendAdminErrorEmail}
          />
        </>
      )}
    </Authenticator>
  );
};

const styles = {
  container: {
    width: 1320,
    maxWidth: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 16px",
  },
  label: { fontSize: 16 },
  optionLabel: { fontSize: 13, fontWeight: 400 },
  count: { fontSize: 16, fontWeight: 500 },
  countBold: { fontSize: 16, fontWeight: 600 },
  headingTitle: { fontSize: 25, lineHeight: 1.2 },
  headingSubtitle: { marginTop: 4, fontSize: 16, lineHeight: 1.4 },
  searchBox: { width: "100%", marginBottom: "24px" },
  dateFilterBar: {
    backgroundColor: "#ffffff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "12px 16px",
    marginBottom: "16px",
  },
  dateFilterLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#323232",
    marginRight: 4,
  },
  datePresetButton: {
    background: "#f5f5f5",
    border: "1px solid #ccc",
    borderRadius: "999px",
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    color: "#323232",
    outline: "none",
    boxShadow: "none",
  },
  datePresetButtonActive: {
    background: "#003439",
    borderColor: "#003439",
    color: "#ffffff",
  },
  dateInputLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#595959",
  },
  signOut: { marginRight: 10 },
  refreshButton: {
    background: "none",
    border: "none",
    padding: 6,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#003439",
    outline: "none",
    boxShadow: "none",
    flexShrink: 0,
  },
  link: { color: "#00838f", textDecoration: "underline" },
  linkButton: {
    background: "none",
    border: "none",
    padding: 0,
    color: "#00838f",
    textDecoration: "underline",
    cursor: "pointer",
    font: "inherit",
  },
  logoCell: {
    textAlign: "center",
    verticalAlign: "middle",
  },
  logoLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    margin: "0 auto",
    outline: "none",
    boxShadow: "none",
  },
  salesforceLogo: {
    height: 22,
    width: 32,
    display: "block",
  },
  goodleapMark: {
    height: 24,
    width: 40,
    display: "block",
  },
  iconButton: {
    background: "none",
    border: "none",
    padding: 4,
    margin: "0 auto",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#003439",
    outline: "none",
    boxShadow: "none",
  },
  deleteConfirm: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    whiteSpace: "nowrap",
  },
  deleteConfirmButton: {
    background: "none",
    border: "none",
    padding: "2px 4px",
    cursor: "pointer",
    color: "#c62828",
    fontSize: 11,
    fontWeight: 600,
    textDecoration: "underline",
    outline: "none",
    boxShadow: "none",
  },
  deleteCancelButton: {
    background: "none",
    border: "none",
    padding: "0 2px",
    cursor: "pointer",
    color: "#666",
    fontSize: 14,
    lineHeight: 1,
    outline: "none",
    boxShadow: "none",
  },
  emailSentCell: {
    textAlign: "center",
    verticalAlign: "middle",
    minWidth: 96,
    whiteSpace: "nowrap",
  },
  emailSentLabel: {
    textDecoration: "underline",
    fontWeight: 600,
    fontSize: 13,
  },
  emailSentSent: {
    color: "#2e7d32",
  },
  emailSentFailed: {
    color: "#c62828",
  },
  emailSentNotSent: {
    color: "#595959",
  },
  resolutionCell: {
    textAlign: "center",
    verticalAlign: "middle",
    minWidth: 108,
    whiteSpace: "nowrap",
  },
  categoryBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
};

export default AdminErrorLogs;
